import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  Alert,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import { 
  Google as GoogleIcon,
  Description as DocumentIcon,
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';

interface ShareGoogleDriveModalProps {
  open: boolean;
  onClose: () => void;
  responseText: string;
  responseImage?: string;
  onHideModals?: () => void;
  onShowModals?: () => void;
  showModal?: boolean;
}

interface GoogleDoc {
  id: string;
  name: string;
}

type Step = 'choose' | 'create' | 'append' | 'success' | 'error' | 'loading' | 'auth';

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

// Helper to get cookie value
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

const ShareGoogleDriveModal: React.FC<ShareGoogleDriveModalProps> = ({ 
  open, 
  onClose, 
  responseText, 
  responseImage, 
  onHideModals, 
  onShowModals,
  showModal = true
}) => {
  // Debug logs removed to reduce excessive rendering
  
  const [step, setStep] = useState<Step>('choose');
  const [docs, setDocs] = useState<GoogleDoc[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successUrl, setSuccessUrl] = useState<string>('');

  // Simulate auth check (replace with real check if needed)
  const [isAuthed, setIsAuthed] = useState<boolean>(true);

  useEffect(() => {
    if (open) {
      setError('');
      setSuccessUrl('');
      setTitle('');
      // Don't reset step or selectedDoc when modal is restored
    }
  }, [open]);

  // Preserve step when modals are restored (don't reset to 'choose')
  useEffect(() => {
    if (open && step === 'choose' && selectedDoc) {
      // If we have a selected doc, we should be in append step
      setStep('append');
    }
  }, [open, step, selectedDoc]);

  // Fetch docs when needed
  const fetchDocs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/google-drive/documents`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch documents');
      const data = await res.json();
      setDocs(data.documents || []);
    } catch (e: any) {
      setError(e.message || 'Error fetching documents');
    } finally {
      setLoading(false);
    }
  };

  // Format content with image link (same as SMS share)
  const getFormattedContent = () => {
    if (responseImage) {
      return `Image: ${responseImage}\n\n${responseText}`;
    }
    return responseText;
  };

  // Extract base64 data from data URL
  const getBase64Data = (dataUrl: string): string => {
    if (dataUrl.startsWith('data:')) {
      return dataUrl.split(',')[1];
    }
    return dataUrl;
  };

  // Fetch image from URL and convert to base64
  const fetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // Extract base64 part from data URL
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Failed to fetch image:', error);
      throw error;
    }
  };

  // Handle create new doc
  const handleCreate = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Creating document with responseImage:', responseImage);
      console.log('includeImage will be:', !!responseImage);
      
      const res = await fetch(`${API_BASE_URL}/google-drive/documents/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title,
          content: getFormattedContent(),
          includeImage: !!responseImage,
          imageUrl: responseImage || undefined
        })
      });
      if (!res.ok) throw new Error('Failed to create document');
      const data = await res.json();
      setSuccessUrl(data.documentUrl);
      setStep('success');
    } catch (e: any) {
      setError(e.message || 'Error creating document');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  // Handle append to doc
  const handleAppend = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Appending to document with responseImage:', responseImage);
      console.log('includeImage will be:', !!responseImage);
      
      const res = await fetch(`${API_BASE_URL}/google-drive/documents/append`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          documentId: selectedDoc,
          content: getFormattedContent(),
          includeImage: !!responseImage,
          imageUrl: responseImage || undefined
        })
      });
      if (!res.ok) throw new Error('Failed to append to document');
      const data = await res.json();
      setSuccessUrl(data.documentUrl);
      setStep('success');
    } catch (e: any) {
      setError(e.message || 'Error appending to document');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  // Handle auth (placeholder)
  const handleAuth = () => {
    window.location.href = `${API_BASE_URL}/google-drive/auth`;
  };

  // Handle Google Drive re-authentication
  const handleReconnect = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/google-drive/auth/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.authUrl) {
        window.open(data.authUrl, '_blank', 'noopener');
      } else {
        alert('Failed to get Google auth URL.');
      }
    } catch (e) {
      alert('Failed to initiate Google Drive authentication.');
    }
  };

  // Load Google Picker script
  const loadPickerScript = () => {
    return new Promise<void>((resolve, reject) => {
      if (window.gapi) return resolve();
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => resolve();
      script.onerror = () => reject('Failed to load Google API script');
      document.body.appendChild(script);
    });
  };

  // Get access token from backend
  const fetchAccessToken = async () => {
    const res = await fetch(`${API_BASE_URL}/google-drive/token`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to get access token');
    const data = await res.json();
    return data.accessToken;
  };

  const handlePickDocument = () => {
    // Hide both modals before opening the picker
    if (onHideModals) {
      onHideModals();
    }
    // Small delay to ensure modals are hidden before picker opens
    setTimeout(() => {
      openPicker();
    }, 100);
  };

  // Open Google Picker
  const openPicker = async () => {
    setLoading(true);
    setError('');
    try {
      await loadPickerScript();
      window.gapi.load('picker', async () => {
        const accessToken = await fetchAccessToken();
        const picker = new window.google.picker.PickerBuilder()
          .addView(window.google.picker.ViewId.DOCS)
          .setOAuthToken(accessToken)
          .setDeveloperKey(process.env.REACT_APP_GOOGLE_API_KEY)
          .setCallback((data: any) => {
            console.log('Google Picker callback:', data.action);
            
            // Only show modals again when picker is actually closed
            if (data.action === window.google.picker.Action.PICKED || 
                data.action === window.google.picker.Action.CANCEL) {
              console.log('Picker closed, showing modals again');
              // Small delay to ensure picker is fully closed
              setTimeout(() => {
                if (onShowModals) {
                  onShowModals();
                }
              }, 200);
            }
            
            if (data.action === window.google.picker.Action.PICKED) {
              setSelectedDoc(data.docs[0].id);
            }
          })
          .build();
        picker.setVisible(true);
      });
    } catch (e: any) {
      setError(e.message || 'Error loading Picker');
      // Show modals again on error
      if (onShowModals) {
        onShowModals();
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle actual modal close by user
  const handleModalClose = () => {
    setStep('choose');
    setSelectedDoc('');
    setError('');
    setSuccessUrl('');
    setTitle('');
    onClose();
  };

  // Step rendering
  let content;
  if (!isAuthed) {
    content = (
      <Box textAlign="center" py={3}>
        <Typography variant="body1" sx={{ mb: 2 }}>Connect your Google Drive account to continue.</Typography>
        <Button variant="contained" onClick={handleAuth}>Connect Google Drive</Button>
      </Box>
    );
  } else if (step === 'choose') {
    content = (
      <Box py={3}>
        <Typography variant="h6" sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
          Share to Google Drive
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          Choose how you'd like to save your AI response
        </Typography>
        
        <Stack spacing={2}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}
            onClick={() => setStep('create')}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AddIcon />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Create New Document
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start fresh with a new Google Doc
                </Typography>
              </Box>
            </Box>
          </Paper>
          
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}
            onClick={() => { fetchDocs(); setStep('append'); }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  bgcolor: 'secondary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <EditIcon />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Append to Existing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add to an existing Google Doc
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Stack>
      </Box>
    );
  } else if (step === 'create') {
    content = (
      <Box py={2}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Create New Document
        </Typography>
        
        <TextField
          label="Document Title"
          fullWidth
          value={title}
          onChange={e => setTitle(e.target.value)}
          sx={{ mb: 3 }}
          placeholder="Enter a title for your document..."
        />
        
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Content Preview
        </Typography>
        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            value={getFormattedContent()}
            disabled
            variant="outlined"
            sx={{ 
              '& .MuiInputBase-root': {
                bgcolor: 'transparent'
              }
            }}
          />
        </Paper>
        
        {responseImage && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Image Preview
            </Typography>
            <Paper elevation={2} sx={{ p: 1, display: 'inline-block' }}>
              <img 
                src={responseImage} 
                alt="AI response preview" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 180, 
                  borderRadius: 8,
                  display: 'block'
                }} 
              />
            </Paper>
          </Box>
        )}
        
        <Button 
          variant="contained" 
          onClick={handleCreate} 
          disabled={!title || loading} 
          fullWidth
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <DocumentIcon />}
          sx={{ 
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          {loading ? 'Creating...' : 'Create & Share Document'}
        </Button>
      </Box>
    );
  } else if (step === 'append') {
    content = (
      <Box py={2}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Append to Existing Document
        </Typography>
        
        <Button 
          variant="outlined" 
          onClick={handlePickDocument} 
          disabled={loading} 
          sx={{ mb: 3 }}
          startIcon={<GoogleIcon />}
          size="large"
          fullWidth
        >
          {selectedDoc ? 'Change Document' : 'Select Google Document'}
        </Button>
        
        {selectedDoc && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <DocumentIcon />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Document Selected
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              ID: {selectedDoc}
            </Typography>
          </Paper>
        )}
        
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Content Preview
        </Typography>
        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            value={getFormattedContent()}
            disabled
            variant="outlined"
            sx={{ 
              '& .MuiInputBase-root': {
                bgcolor: 'transparent'
              }
            }}
          />
        </Paper>
        
        {responseImage && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Image Preview
            </Typography>
            <Paper elevation={2} sx={{ p: 1, display: 'inline-block' }}>
              <img 
                src={responseImage} 
                alt="AI response preview" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 180, 
                  borderRadius: 8,
                  display: 'block'
                }} 
              />
            </Paper>
          </Box>
        )}
        
        <Button 
          variant="contained" 
          onClick={handleAppend} 
          disabled={!selectedDoc || loading} 
          fullWidth
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
          sx={{ 
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          {loading ? 'Appending...' : 'Append & Share'}
        </Button>
      </Box>
    );
  } else if (step === 'success') {
    content = (
      <Box textAlign="center" py={4}>
        <Box 
          sx={{ 
            p: 2, 
            borderRadius: '50%', 
            bgcolor: 'success.main',
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}
        >
          <SuccessIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: 'success.main' }}>
          Document Created Successfully!
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Your AI response has been saved to Google Drive
        </Typography>
        <Button 
          variant="contained" 
          href={successUrl} 
          target="_blank" 
          rel="noopener"
          startIcon={<DocumentIcon />}
          size="large"
          sx={{ 
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          Open Google Doc
        </Button>
      </Box>
    );
  } else if (step === 'error') {
    content = (
      <Box textAlign="center" py={4}>
        <Box 
          sx={{ 
            p: 2, 
            borderRadius: '50%', 
            bgcolor: 'error.main',
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}
        >
          <ErrorIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
          Something went wrong
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button 
            variant="contained" 
            onClick={() => setStep('choose')}
            startIcon={<EditIcon />}
          >
            Try Again
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleReconnect}
            startIcon={<GoogleIcon />}
          >
            Reconnect
          </Button>
        </Stack>
      </Box>
    );
  } else if (step === 'loading') {
    content = (
      <Box textAlign="center" py={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Dialog 
      open={open && showModal} 
      onClose={handleModalClose} 
      fullWidth 
      maxWidth="sm" 
      sx={{ 
        zIndex: 2001,
        '& .MuiBackdrop-root': {
          display: showModal ? 'block' : 'none'
        },
        '& .MuiDialog-paper': {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        '& .MuiTypography-root': {
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <GoogleIcon sx={{ color: 'primary.main' }} />
          Share to Google Drive
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        {content}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleModalClose} 
          color="secondary"
          sx={{ 
            fontWeight: 500,
            textTransform: 'none'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareGoogleDriveModal; 